import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// `withCredentials` is REQUIRED for the httpOnly auth cookie (Phase C)
// to be sent on every request. Same-origin here so no CORS drama —
// the same flag also makes the browser accept the Set-Cookie header
// coming back from /auth/mobile/login and /auth/mobile/register.
export const api = axios.create({ baseURL: API, withCredentials: true });
// Any raw axios usage in the codebase (there's a couple in auth.jsx
// that intentionally hit /auth/config and /auth/mobile/login before the
// AuthProvider is ready) needs cookies too. Setting the default on the
// module-level axios keeps them in lock-step.
axios.defaults.withCredentials = true;

// Global mutation broadcast — every successful non-GET response fires a
// `ych:data-changed` custom event so sibling views (DataSpreadsheetView,
// App-level sailor summary, etc.) auto-refresh instead of showing stale
// rows until the coach manually switches tabs. Previously the event was
// only fired from a handful of callsites (DataEditor, ArchiveModal,
// DataQualityModal) which meant biometric cell edits, bulk deworming,
// baseline patches, supplement/injury saves etc. never triggered a
// refresh. Centralising here covers every current + future mutation
// path in one line.
const _MUTATE_METHODS = new Set(["post", "put", "patch", "delete"]);
const _SKIP_BROADCAST_PATHS = [
  "/auth/",            // login/register/logout — auth state has its own handling
  "/help/ask",         // Claude help assistant — not a data mutation
  "/help/log",
];
api.interceptors.response.use((response) => {
  try {
    const method = (response?.config?.method || "").toLowerCase();
    const url = response?.config?.url || "";
    if (_MUTATE_METHODS.has(method) && !_SKIP_BROADCAST_PATHS.some((p) => url.includes(p))) {
      window.dispatchEvent(new CustomEvent("ych:data-changed", { detail: { url, method } }));
    }
  } catch {
    /* never let broadcast failure break the response chain */
  }
  return response;
});

export const GROUP_COLORS = {
  "Current Athletes": "#00E5FF",
  "MJPT Athletes":    "#F59E0B",
  "Old Sailors":      "#8B5CF6",
  "Staff":            "#FF7A6B",
};

export const SERIES_COLORS = [
  "#00E5FF", "#F59E0B", "#8B5CF6", "#EF4444",
  "#10B981", "#FACC15", "#FF7A6B", "#60A5FA",
];

export const STATUS_LABEL = {
  ok: "In range",
  low: "Low",
  high: "High",
  na: "N/A",
};

/**
/**
 * Central date formatter used everywhere in the app. Output format is
 * `DD/MM/YYYY` (Indian convention, matches how coaches / doctors read
 * dates). Coaches asked for the full 4-digit year on every screen so
 * "23/05/26" doesn't get confused with "1926" during long-range chart
 * scrubbing.
 *
 * Accepts either an ISO date (`YYYY-MM-DD`) or an ISO datetime; the time
 * portion is ignored. Returns `—` for null / empty / malformed input.
 */
export function fmtDate(iso) {
  if (!iso) return "—";
  const raw = String(iso).slice(0, 10); // strip time if present
  const [y, m, d] = raw.split("-");
  if (!y || !m || !d) return raw;
  return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
}

/** Long form: `DD MMM YYYY` — for report headers and print output where
 *  short dd/mm/yyyy is too dense. Rarely used; keep `fmtDate` for UI. */
export function fmtDateLong(iso) {
  if (!iso) return "—";
  const raw = String(iso).slice(0, 10);
  const [y, m, d] = raw.split("-");
  if (!y || !m || !d) return raw;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${parseInt(d,10)} ${months[parseInt(m,10)-1]} ${y}`;
}

/**
 * `DD/MM/YYYY HH:MM` — for audit / "last saved at" columns where we
 * need the wall-clock time alongside the date. Any ISO datetime string
 * accepted; missing time returns just the date portion.
 */
export function fmtDateTime(iso) {
  if (!iso) return "—";
  const s = String(iso);
  const datePart = s.slice(0, 10);
  const timePart = s.slice(11, 16); // HH:MM from ISO "2026-07-06T04:22:42..."
  const [y, m, d] = datePart.split("-");
  if (!y || !m || !d) return s;
  const dmy = `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
  return timePart ? `${dmy} ${timePart}` : dmy;
}

export function fmtNum(v, digits = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  if (Math.abs(v) >= 100) return v.toFixed(0);
  return v.toFixed(digits);
}
